import { useContext, useMemo, useState } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { SampleFilterBar, SampleFilterForm } from "./SampleFilterBar";
import {
  Sample,
  SampleFilters,
  SampleFiltersTranslator,
  samplesConstants,
  SampleSearchTermOptions,
} from "../../api/Samples";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { ExclusiveDropdown } from "../../common/buttons/ExclusiveDropdown/ExclusiveDropdown";
import { GenericBooleanStateProvider } from "../../common/modals/Modal/GenericModal";
import { SamplesBulkEditDropDownWrapper } from "./SamplesBulkEditDropDown";
import { GetPersons, TableArrayRenderer } from "../../common/misc/EntityRenders/EntityRenderer";
import { ExportCsvButton } from "../../common/tables/ExportCsvButton/ExportCsvButton";
import { AddCustomTypeEntity } from "../../Customization/CustomTypes/generics/AddCustomTypeEntity";
import { useCustomTypesEntityTable } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import SearchInputWithOptions from "../../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import { renderCustomTypeColumn } from "../../Customization/CustomTypes/generics/CustomTypeRenderer";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableCloneButton } from "../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableRestoreButton } from "../../common/entity/entityComponents/EntityTableRestoreButton";
import { EntityTableSoftDeletableButton } from "../../common/entity/entityComponents/EntityTableSoftDeletableButton";
import { GenericEntityTableComplianceDropdown } from "../../Entities/GenericComponents/common/GenericEntityTable";
import { ExportPdfButton } from "../../common/tables/ExportPdfButton/ExportPdfButton";
import { NestedDropdown } from "../../common/buttons/NestedDropdown/NestedDropdown";
import { EntityOrderBy, InferSortByOptions, ISearchTermParametersDefaults } from "../../api/GenericTypes";
import { NotSet } from "../../common/misc/UIconstants";

export const switchSamplesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<SampleFilters>>[] | null | undefined
): SortState<InferSortByOptions<SampleFilters>>[] => {
  return (
    genericDefaultSortState(sortState) ?? [
      {
        headerId: "default-enteredOn",
        sortDirection: "DESC",
        property: "ENTERED_ON",
      },
    ]
  );
};

export const defaultSamplesFilters: SampleFilters = {
  sortBy: [
    {
      property: "ENTERED_ON",
      sortDirection: "DESC",
    },
  ],
  ...ISearchTermParametersDefaults,
};

export const useSamplesTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"samples">) => {
  const { session } = useContext(SessionContext);

  const defaults: ColumnsSettings<Sample> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 2, active: true, header: fieldLabels.name, property: "name" },
      "default-customType": { pos: 7, active: true, header: fieldLabels.customType, property: "customType" },
      "default-projects": { pos: 8, active: true, header: fieldLabels.projects, property: "projects" },
      "default-owner": { pos: 9, active: true, header: fieldLabels.owner, property: "owner" },
      "default-enteredOn": { pos: 10, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 11, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 12, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 13, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [
      fieldLabels.id,
      fieldLabels.name,
      fieldLabels.customType,
      fieldLabels.projects,
      fieldLabels.owner,
      fieldLabels.enteredOn,
      fieldLabels.enteredBy,
      fieldLabels.modifiedOn,
      fieldLabels.modifiedBy,
    ]
  );

  const tabStoreDefaults: TableTabsDict<Sample, SampleFilters, SampleFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultSamplesFilters,
          sidebarFilters: { includeSoftDeleted: false },
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      mydata: {
        tabId: "mydata",
        type: "fixed",
        label: `My ${samplesConstants.entityPlural}`,
        title: `My ${samplesConstants.entityPlural}`,
        icon: "user",
        align: "left",
        xPos: 1,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultSamplesFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { ownerIds: session?.person.id ? [session.person.id] : null },
          sidebarFilters: {},
        },
      },
      trash: {
        tabId: "trash",
        type: "fixed",
        label: "Trash",
        title: "Trash",
        icon: "trash",
        align: "left",
        xPos: 3,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultSamplesFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { isSoftDeleted: true },
          sidebarFilters: {},
        },
      },
    }),
    [session?.person.id]
  );
  return { defaults, tabStoreDefaults };
};

export const useSamplesTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
}: UseITypedEntityTableProps<"samples">) => {
  // Custom Fields
  const { columnSettings, customFieldHeaders } = useCustomTypesEntityTable({
    defaultColumnSettings: defaults,
    entityType: "Sample",
    customTypeIds: filters?.customTypeIds ?? undefined,
    sort,
    setSort,
  });

  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<Sample> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 120,
        minWidth: 120,
        align: "right",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.projects,
        id: "default-projects",
        accessor: (row) => <TableArrayRenderer values={row.projects}>{(value) => value.name}</TableArrayRenderer>,
        width: 200,
        align: "left",
      },
      {
        id: "default-owner",
        Header: fieldLabels.owner,
        accessor: (row) => (row.owner ? <GetPersons persons={[row.owner]} createLinks={false} /> : NotSet),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("OWNER", sort, setSort),
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={[row.enteredBy]} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    // Add custom field headers
    headers.push(renderCustomTypeColumn(entityConstants, sort, setSort));
    headers = headers.concat(customFieldHeaders);
    return headers;
  }, [
    customFieldHeaders,
    entityConstants,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.id,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    fieldLabels.owner,
    fieldLabels.projects,
    setSort,
    sort,
  ]);

  return { columns, columnSettings };
};

export const SamplesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"samples">) => {
  const { session } = useContext(SessionContext);
  const {
    bulkRestoreMutationAsync,
    bulkDeleteMutationAsync,
    isLoadingBulkDeleteMutation,
    isLoadingBulkRestoreMutation,
  } = entityApi;
  const [showModal, setShowModal] = useState(false);

  const { defaults, tabStoreDefaults } = useSamplesTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<Sample>();

  const {
    filters,
    forcedFilters,
    sidebarFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<Sample, SampleFilters, SampleFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchSamplesDefaultSortState,
      dispatchTabStore,
    });

  const { columns, columnSettings } = useSamplesTableColumns({
    fieldLabels,
    entityConstants,
    defaults,
    filters,
    sort,
    setSort,
  });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          {/* <AddSampleButton /> */}
          <AddCustomTypeEntity
            entityConstants={entityConstants}
            canCreateEntityPermission={permissions.canCreate}
            entityType={"Sample"}
          />
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <SampleFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<Sample, SampleFilters>
                filters={filters}
                translatorConsts={SampleFiltersTranslator}
                excludeFilters={{
                  includeSoftDeleted: (value) => value === true,
                  isSoftDeleted: () => false,
                  searchTermIncludeIds: () => false,
                  searchTermIncludeNames: () => false,
                  searchTermIncludeUuids: () => false,
                  searchTermIncludeCustomFieldValues: () => false,
                }}
              />
              {/* <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" /> */}
              <SearchInputWithOptions
                searchTermOptions={SampleSearchTermOptions}
                filters={filters}
                setFilters={async (newFilters) => {
                  await dispatchTabStore({
                    type: "setTab",
                    options: { keepPrevious: true },
                    payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                  });
                }}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />
              <div
                className="flex row-nowrap align-center gap-5"
                style={{ height: "min-content", marginBottom: "auto" }}
              >
                {currentTab !== "trash" && (
                  <GenericBooleanStateProvider>
                    {({ isTrue: showModal, setIsTrue: setShowModal }) => (
                      <ExclusiveDropdown
                        show={showModal}
                        setShow={setShowModal}
                        disabled={
                          selection.size === 0 ||
                          (session?.features.development_mode ? false : selection.size > 1000) ||
                          !permissions.canBulkEdit(selectionPermissions)
                        }
                        title={`${
                          !permissions.canBulkEdit(selectionPermissions)
                            ? `Insufficient permissions to edit selected ${entityConstants.entityPlural}`
                            : selection.size > 1000
                            ? `Editing is only allowed for max. 1000 ${entityConstants.entityPlural} at once`
                            : `Edit`
                        }`}
                        btnCls={"btn btn-default"}
                        btnLabel={"Edit"}
                        icon={<LucideIcon name="square-pen" />}
                      >
                        <SamplesBulkEditDropDownWrapper
                          selection={selection}
                          onSuccess={() => {
                            functionRef.current?.resetSelection();
                            setShowModal(false);
                          }}
                          onClose={() => {
                            setShowModal(false);
                          }}
                          filters={filters}
                          entityConstants={entityConstants}
                        />
                      </ExclusiveDropdown>
                    )}
                  </GenericBooleanStateProvider>
                )}
                <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                  <li>
                    <EntityTableCloneButton
                      entityConstants={entityConstants}
                      routes={routes}
                      permissions={permissions}
                      selection={selection}
                    />
                  </li>
                  <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                    <li>
                      <ExportCsvButton
                        entityConstants={entityConstants}
                        columnSettings={columnSetting}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                    <li>
                      <ExportPdfButton
                        entityConstants={entityConstants}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                  </NestedDropdown>
                  {/* Locking & Signing */}
                  <GenericEntityTableComplianceDropdown
                    entityTypeId={entityTypeId}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    tableFunctionRef={functionRef}
                    currentTab={currentTab}
                    showSignButton
                  />

                  {/* Trash */}
                  {currentTab === "trash" && (
                    <li>
                      <EntityTableRestoreButton
                        entityConstants={entityConstants}
                        selection={selection}
                        permissions={permissions}
                        selectionPermissions={selectionPermissions}
                        onClick={async () =>
                          await bulkRestoreMutationAsync({ ids: Array.from(selection) }).catch(() => {})
                        }
                        loading={isLoadingBulkRestoreMutation}
                      />
                    </li>
                  )}
                  <li className="divider" />
                  <li>
                    <EntityTableSoftDeletableButton
                      currentTab={currentTab}
                      entityConstants={entityConstants}
                      selection={selection}
                      permissions={permissions}
                      selectionPermissions={selectionPermissions}
                      onClick={() => setShowModal(true)}
                    />
                  </li>
                </MoreDropdown>
              </div>
              <AlertModal
                type={`${currentTab === "trash" ? "danger" : "warning"}`}
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select dataset to ${currentTab === "trash" ? "delete" : "trash"}`
                    : selection.size === 1
                    ? `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entitySingular}?`
                    : `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entityPlural}?`
                }`}
                description={`${
                  currentTab === "trash"
                    ? `Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`
                    : `Proceeding will move the selected ${entityConstants.entityPlural} into trash.`
                }`}
                proceedLabel={`${currentTab === "trash" ? "Delete" : "Trash"}`}
                onProceed={async () => {
                  await bulkDeleteMutationAsync({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.entityPlural,
                    params: { deletePermanently: currentTab === "trash" },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<Sample, SampleFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={columnSettings}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
